/**
 * URL construction & sanitization for thumbnail rendering.
 *
 * YouTube's `img.youtube.com` and `i.ytimg.com` CDNs return a soft-404
 * (HTTP 404 with a valid 120×90 JPEG body) for any video ID that begins
 * with `upload_`. These IDs originate in Echonour's own private-upload
 * pipeline and are never valid YouTube IDs. Because the browser decodes
 * the soft-404 body successfully, `<img onError>` does not fire, so the
 * shared `<Thumbnail>` component cannot detect the failure at render time.
 *
 * The fix is to filter such URLs before they reach the component. This
 * file is the single chokepoint where every thumbnail URL the app
 * constructs or consumes is normalized to either a real URL or `null`.
 */

/**
 * Matches the synthetic-upload-ID shape on either YouTube CDN host:
 *   - https://i.ytimg.com/vi/upload_<anything>/...
 *   - https://img.youtube.com/vi/upload_<anything>/...
 * Anchored to (scheme, host, /vi/, upload_) so it cannot accidentally
 * match a URL that happens to contain the substring "upload_" elsewhere.
 */
const UPLOAD_SOFT_404_RE =
  /^https?:\/\/(?:i\.ytimg\.com|img\.youtube\.com)\/vi\/upload_/i;

/**
 * Returns the URL unchanged unless it is a known soft-404 shape, in
 * which case it returns `null`. Falsy inputs round-trip as `null`.
 */
export function sanitizeThumbnailUrl(
  url: string | null | undefined,
): string | null {
  if (!url) return null;
  return UPLOAD_SOFT_404_RE.test(url) ? null : url;
}

/**
 * Item shape consumed by getItemThumbnailUrl. Loose by design — the
 * helper only reads two fields and tolerates extras.
 */
export interface ThumbnailItem {
  thumbnail_url?: string | null;
  video_id?: string | null;
}

/**
 * Picks the best thumbnail URL for an item:
 *   1. If `thumbnail_url` is truthy and not a soft-404 shape → use it.
 *   2. Else, if `video_id` is a real YouTube ID (no `rss_` or `upload_`
 *      prefix) → derive `https://i.ytimg.com/vi/<id>/mqdefault.jpg`.
 *   3. Else → `null`.
 *
 * Callers pass the result to `<Thumbnail src={…}>`; a `null` causes the
 * component to render its inline cover-art SVG.
 */
export function getItemThumbnailUrl(item: ThumbnailItem): string | null {
  const persisted = sanitizeThumbnailUrl(item.thumbnail_url);
  if (persisted) return persisted;

  const videoId = item.video_id ?? null;
  if (
    videoId &&
    !videoId.startsWith("rss_") &&
    !videoId.startsWith("upload_")
  ) {
    return `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
  }
  return null;
}
